import './searchbar.css';

import {
  IonButton,
  IonButtons,
  IonIcon,
  IonList,
  IonSearchbar,
  IonText,
  IonToolbar,
  useIonPopover,
} from '@ionic/react';
import { filter } from 'ionicons/icons';
import React, {
  FunctionComponent,
  ReactElement,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useClickAway } from 'react-use';
import {
  InfiniteScrollOptions,
  useSearchListQueryWithInfiniteScroll,
} from '../../../api/hooks/infinite-scroll.hooks';
import { trackSearchLaunched } from '../../../config/analytics';
import i18n from '../../../i18n';
import { useKeyboardIsShowing } from '../../../theme/keyboard.hook';
import { LoadingRetry } from '../../graphql/LoadingRetry';

interface SearchbarProps {
  onSearch: (searchTerm: string | undefined) => void;
  filterPopoverContent?:
    | ReactElement<SearchbarPopoverChildrenProps>
    | FunctionComponent<SearchbarPopoverChildrenProps>;
  showFilterIndicator?: boolean;
  ionSearchbarProps?: React.ComponentProps<typeof IonSearchbar>;
}

export interface SearchbarRef {
  setSearchText: (text?: string) => void;
}

export interface SearchbarPopoverChildrenProps {
  close: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface SearchPopoverProps<Query, Input> {
  options: InfiniteScrollOptions<Query, Input>;
  header?: ReactElement;
  rowRenderer: SearchRowRenderer;
  placeholder?: string;
  forceSearchOnDidPresent?: boolean;
}

interface SearchPopoverResult {
  showSearch: () => void;
}

export interface SearchRowProps<T = unknown> {
  item: T;
  index: number;
  dismiss: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SearchRowRenderer<T = any> = (
  props: SearchRowProps<T>
) => ReactElement;

const Searchbar = forwardRef<SearchbarRef, SearchbarProps>(
  (props: SearchbarProps, ref): ReactElement => {
    const {
      onSearch,
      filterPopoverContent,
      showFilterIndicator,
      ionSearchbarProps,
    } = props;

    const toolbarRef = useRef<HTMLIonToolbarElement>(null);
    const searchbarRef = useRef<HTMLIonSearchbarElement>(null);
    const isKeyboardShowing = useKeyboardIsShowing();

    useImperativeHandle(ref, (): SearchbarRef => {
      return {
        setSearchText: async (text?: string) => {
          const el = await searchbarRef.current?.getInputElement();
          if (el) {
            el.value = text ?? '';
          }
        },
      };
    });

    const onChange = (event: CustomEvent) => {
      const value = event.detail.value === '' ? undefined : event.detail.value;
      onSearch(value);
    };

    useClickAway(searchbarRef, async () => {
      const el = await searchbarRef.current?.getInputElement();
      if (el && isKeyboardShowing) {
        el.blur();
      }
    });

    const childProps: SearchbarPopoverChildrenProps = {
      close: () => {
        if (typeof dismiss === 'function') {
          dismiss();
        }
      },
    };

    const [present, dismiss] = useIonPopover(
      filterPopoverContent ?? <></>,
      childProps
    );

    return (
      <IonToolbar className="searchbar-wrapper" ref={toolbarRef}>
        <IonSearchbar
          ref={searchbarRef}
          debounce={500}
          showCancelButton="never"
          showClearButton="always"
          onIonInput={onChange}
          onIonClear={() => onSearch(undefined)}
          {...ionSearchbarProps}
        ></IonSearchbar>
        {filterPopoverContent && (
          <IonButtons slot="end">
            <IonButton onClick={(e) => present({ event: e.nativeEvent })}>
              {showFilterIndicator && <div className="notification-dot" />}
              <IonIcon icon={filter} slot="icon-only" size="medium" />
            </IonButton>
          </IonButtons>
        )}
      </IonToolbar>
    );
  }
);

Searchbar.displayName = 'Searchbar';

const useSearchPopover = <Query, Input>(
  props: SearchPopoverProps<Query, Input>
): SearchPopoverResult => {
  const { header, options, rowRenderer, placeholder, forceSearchOnDidPresent } =
    props;

  const [hasBeenShown, setHasBeenShown] = useState<boolean>(false);

  const searchbarRef = useRef<HTMLIonSearchbarElement>(null);

  const focusSearch = useCallback(async () => {
    searchbarRef.current?.setFocus();
  }, []);

  const {
    result: { refetch, error, loading },
    search,
    items,
  } = useSearchListQueryWithInfiniteScroll({
    ...options,
    queryOptions: {
      ...options?.queryOptions,
      skip: !hasBeenShown,
    },
  });

  const noResults = (
    <div className="ion-padding ion-text-center">
      <IonText color="medium">{i18n.t('common:no_results_found')}</IonText>
    </div>
  );

  const filteredItems = items ?? [];

  const [show, dismiss] = useIonPopover(
    <div>
      {header}
      <Searchbar
        onSearch={search}
        ionSearchbarProps={{
          placeholder,
          ref: searchbarRef,
        }}
      />
      <LoadingRetry
        refetch={refetch}
        error={error}
        loading={loading}
        results={{
          hasResults: items.length > 0,
          noResultsComponent: noResults,
        }}
      >
        {filteredItems.length > 0 && (
          <IonList lines="full">
            {filteredItems.map((item, index) =>
              rowRenderer({ item, index, dismiss: () => dismiss() })
            )}
          </IonList>
        )}
      </LoadingRetry>
    </div>
  );

  return {
    showSearch: () => {
      trackSearchLaunched();
      setHasBeenShown(true);
      show({
        cssClass: 'search-popover',
        onDidPresent: () => {
          focusSearch();

          if (forceSearchOnDidPresent) {
            refetch();
          }
        },
      });
    },
  };
};

export { Searchbar, useSearchPopover };
